import { useCallback, useState } from 'react';
import type { StatusKind } from '@/features/common/types/StatusKind';

/**
 * The `role="status"` line a screen writes its outcomes to, and whether the last one was a success
 * or a failure — the pair several cabinet and administration screens declared side by side, three
 * of them as a hook of their own before this one.
 *
 * Every one of the three setters writes both halves, so a failure can never be painted in the
 * success colour by a caller that set the text and forgot the kind. `report`/`announce` take the
 * kind as an argument, for the callers that decide it at run time; `succeed`/`fail` read better
 * where it is known.
 *
 * The setters are memoised because callers put them in effects: `useState`'s own setter is stable
 * and a plain closure here is not, so without this a screen that clears the line when its subject
 * changes either re-ran its effect on every render or had to lie in its dependency list.
 */
export function useStatusLine() {
  const [status, setStatus] = useState('');
  const [statusKind, setStatusKind] = useState<StatusKind>('success');

  const report = useCallback((kind: StatusKind, message: string) => {
    setStatusKind(kind);
    setStatus(message);
  }, []);

  return {
    status,
    statusKind,
    report,
    /** The same, with the arguments the other way round — what the events screens pass around. */
    announce: useCallback((message: string, kind: StatusKind) => report(kind, message), [report]),
    succeed: useCallback((message: string) => report('success', message), [report]),
    fail: useCallback((message: string) => report('error', message), [report]),
    clear: useCallback(() => setStatus(''), []),
  };
}
